import React, { useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/axios';
import { DynamicForm } from '../../components/common/DynamicForm';

import { PageHero } from '../../components/common/PageHero';

export const CourseDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: course, isLoading, isError } = useQuery({
        queryKey: ['publicCourse', slug],
        queryFn: async () => {
            const response = await apiClient.get(`/public/courses/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        retry: false
    });

    useEffect(() => {
        if (course?.title) {
            document.title = `${course.title} | Mosaik Offenbach`;
        }
    }, [course?.title]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-32 min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isError || !course) {
        return <Navigate to="/kursangebot" replace />;
    }

    return (
        <div className="flex flex-col bg-background min-h-screen pb-16">
            <PageHero
                title={course.title}
                imageUrl={course.imageUrl}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: t('courses.breadcrumb') || 'Kursangebot', to: '/kursangebot' },
                    { label: course.title }
                ]}
            />

            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 w-full -mt-8 relative z-10">

                <div className="bg-white rounded-3xl shadow-sm border border-border p-8 sm:p-12 text-lg leading-relaxed text-foreground/80 whitespace-pre-line">
                    <button
                        onClick={() => navigate('/kursangebot')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('events.back_to_overview')}
                    </button>

                    {/* Horizontal Info Bar Container */}
                    {((course.registrationUrl && course.registrationUrl.trim() !== '') || !course.formDefinitionId) && (
                        <div className="bg-foreground/[0.02] border border-border rounded-3xl p-6 mb-12">
                            <div className="flex flex-col sm:flex-row gap-4">
                                {course.registrationUrl && course.registrationUrl.trim() !== '' ? (
                                    <a 
                                        href={course.registrationUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                                    >
                                        <FileText className="w-5 h-5" />
                                        {t('courses.register_now', 'Jetzt anmelden')}
                                    </a>
                                ) : (
                                    <button 
                                        onClick={() => navigate('/kontakt')}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                                    >
                                        <FileText className="w-5 h-5" />
                                        {t('courses.register_now', 'Jetzt anmelden')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Main Description */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-foreground border-b border-border pb-4">
                            {t('common.description') || 'Beschreibung'}
                        </h2>
                        <div className="prose prose-lg max-w-none text-foreground/80 leading-relaxed mb-12">
                            {course.description}
                        </div>

                        {course.formDefinitionId && (
                            <div className="mt-16 pt-16 border-t border-border">
                                <DynamicForm 
                                    formId={course.formDefinitionId} 
                                    relatedEntityId={course.id}
                                    relatedEntityType="Course"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
